'use client';

import type { MouseEvent } from 'react';
import { useTranslations } from 'next-intl';
import { useQueryState } from 'nuqs';
import { useEffect, useState } from 'react';
import { SiteLogo } from '@/components/entities/SiteLogo/SiteLogo';
import { Icon } from '@/components/entities/Sprite';
import { GM_DRAWER_QUERY_KEY, gmDrawerQueryParser } from '@/components/features/gamification/bars/model/gmDrawerQuery';
import { Button } from '@/components/ui/Button/button';
import { SkeletonParent } from '@/components/ui/Skeleton/skeleton';
import { AppRoutes } from '@/i18n/constants';
import { Link, usePathname } from '@/i18n/routing';
import openModal from '@/modals/utils/openModal';
import { useHasActionableVerification } from '@/queries/verifications/verifications.hooks';
import { useWalletMeQuery } from '@/queries/wallet/wallet.queries';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { checkToken } from '@/redux/slices/userSlice';
import BalancesPopover from './components/BalancesPopover';
import { UserProfile } from './components/UserProfile';
import { useSmartBack } from './utils/handleBack';

// Root pages and landing entry points keep the logo on mobile; every other page shows the back arrow.
const LOGO_ONLY_PATHS: string[] = ['/', AppRoutes.referralInvite];

interface Props {
  isOpenSideBar: boolean;
  setIsOpenSideBar: (isOpen: boolean) => void;
}

export const Header = ({ isOpenSideBar, setIsOpenSideBar }: Props) => {
  const t = useTranslations('header');
  const [isBalancesOpen, setIsBalancesOpen] = useState(false);

  const pathname = usePathname();
  const [drawerView, setDrawerView] = useQueryState(GM_DRAWER_QUERY_KEY, gmDrawerQueryParser);
  // On mobile the back arrow replaces the logo on every inner page. Only roots keep
  // the logo: the home/lobby and the referral invite landing.
  const showBackArrow = !LOGO_ONLY_PATHS.includes(pathname);

  const dispatch = useAppDispatch();
  const userSession = useAppSelector(state => state.user.userSession);
  const showAttentionIcon = useHasActionableVerification();

  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const handleClickProfile = () => {
    if (isOpenSideBar) {
      setIsOpenSideBar(false);
    }
  };

  const handleOpenSideBar = (event: MouseEvent<HTMLAnchorElement>) => {
    if (pathname === '/' && isOpenSideBar) {
      setIsOpenSideBar(false);
    }

    if (pathname === '/' && drawerView !== null) {
      event.preventDefault();
      void setDrawerView(null);
    }
  };

  const balancesPopoverClose = () => {
    setIsBalancesOpen(false);
  };

  const { data: coins } = useWalletMeQuery();

  const handleBack = useSmartBack();

  useEffect(() => {
    dispatch(checkToken());
  }, []);

  return (
    <header className="fixed 3xl:px-0 top-0 left-0 right-0 flex bg-[linear-gradient(195deg,_#4c2959_0%,_#231333_125.12%)] border-b border-main-800 bg-size-[200%] z-40 py-2.5 sm:py-2.5 px-4 lg:pl-5.5 lg:pr-10 h-16">
      <div className="flex 3xl:max-w-388.5 3xl:mx-auto justify-between items-center w-full">
        {showBackArrow
          ? (
              <div className="items-center bg-main-800 rounded-full p-3 lg:hidden size-11 flex justify-center">
                <button type="button" onClick={handleBack} className="flex items-center">
                  <Icon name="arrow-back" className="size-4" />
                </button>
              </div>
            )
          : (
              <Link href="/" className="block lg:hidden" onClick={handleOpenSideBar}>
                <SiteLogo layout="mobile" className="w-12 h-10.5" priority />
              </Link>
            )}

        <Link href="/" className="max-lg:hidden">
          <SiteLogo layout="desktop" className="w-35 h-11" priority />
        </Link>

        {!mounted
          ? (
              <div className="flex gap-2 items-center">
                <SkeletonParent className="w-23.25 h-11 rounded-[3.125rem]" />
                <SkeletonParent className="w-23.25 h-11 rounded-[3.125rem]" />
              </div>
            )
          : (
              <>
                {userSession
                  ? (
                      <>
                        <div className="flex items-center h-11 bg-c900 rounded-full">
                          <BalancesPopover
                            isBalancesOpen={isBalancesOpen}
                            setIsBalancesOpen={setIsBalancesOpen}
                            coins={coins}
                            balancesPopoverClose={balancesPopoverClose}
                          />

                          <Button
                            variant="primary"
                            size="sm"
                            className="p-3.75 rounded-[6.25rem]"
                            onClick={() => openModal('deposit')}
                          >
                            <Icon name="plus" className="size-3.5" />
                          </Button>
                        </div>
                        <UserProfile key={pathname} onClickProfile={handleClickProfile} showAttentionIcon={showAttentionIcon} />
                      </>
                    )
                  : (
                      <div className="flex items-center gap-2">
                        <Button
                          variant="stroked"
                          className="px-6 py-3 text-sm leading-5 font-medium text-white"
                          onClick={() => openModal('login')}
                        >
                          {t('sign-in')}
                        </Button>

                        <Button
                          variant="primary"
                          className=" py-3 px-6 text-sm leading-5 font-medium min-w-24.75"
                          onClick={() => openModal('register')}
                        >
                          {t('sign-up')}
                        </Button>
                      </div>
                    )}
              </>
            )}
      </div>
    </header>
  );
};

export default Header;
